import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PencilRuler, Upload, X } from 'lucide-react';
import { collection, doc } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useIsInsideAppLayout } from '../components/AppLayoutContext';
import Seo from '../seo/Seo';
import { buildPublicToolPageSchemas } from '../seo/structuredData';
import { useAuth } from '../context/AuthContext';
import DesignOutputWorkspace from '../components/designWorkspace/DesignOutputWorkspace';
import ToolSeoSections from '../components/ToolSeoSections';
import CollapsibleSeoContent from '../components/seo/CollapsibleSeoContent';
import { getSeoContent, getSeoFaqSchema } from '../seo/seoContentData';
import DesignHexColorSection from '../components/DesignHexColorSection';
import { useDesignWorkspaceMode } from '../hooks/useDesignWorkspaceMode';
import { useCredits } from '../hooks/useCredits';
import { db } from '../firebase';
import { requestFloorPlanRoomToRealisticImage } from '../lib/requestFloorPlanRoomToRealisticImage';
import { downloadUrlAsFile } from '../lib/downloadUrlAsFile';
import { DESIGN_STYLES, ROOM_TYPES } from '../lib/roomDesignOptions';
import { createEmptyDesignerState, normalizeDesignerOpenings } from '../lib/floorPlanDesignerState';
import { validateDesignerState } from '../lib/floorPlanDesignerToAnalysisJson';
import FloorPlanEditor, { getDesignerDrawingBounds } from '../features/floorPlanEditor/FloorPlanEditor';
import { floorPlanToRoomImageConfig } from '../features/floorPlanEditor/configs/floorPlanToRoomImageConfig';
import RoomRenderSetupLayout from '../features/floorPlanRoomRender/RoomRenderSetupLayout';
import { HAS_CUBICASA_BACKEND_URL } from '../config/cubicasa';
import { requestCubiCasaMaskTest, validateCubiCasaFloorPlanFile } from '../lib/cubicasaDebugApi';
import { importCubiCasaResponseToDesignerState } from '../features/floorPlanEditor/importers/cubicasaImportPipeline';
import { buildCameraPresets, CAMERA_WALL_MARGIN_M } from '../lib/roomRenderCamera';
import { cropConfigFromOpenAiSize } from '../lib/roomCropModes';
import floorPlanRoomRenderIllustration from '../assets/design_tools/floorplan_to_image/ui/2d FloorPlan to Image.webp';

const MAX_FOV = 85;
const DEFAULT_FOV = MAX_FOV;
const GENERATED_IMAGE_QUALITY_STANDARD = 'standard';
const GENERATED_IMAGE_QUALITY_HIGH = 'high';
const DEFAULT_GENERATED_IMAGE_QUALITY = GENERATED_IMAGE_QUALITY_STANDARD;
const DECORATION_CHANGE_LEVELS = ['Light Refresh', 'Balanced Redesign', 'Full Decoration Redesign'];
const MAX_CUSTOM_COLORS = 3;
const MAX_WALL_COLORS = 3;
const ANALYZING_MESSAGE_DEFAULT = 'Detecting walls, doors, windows, and rooms.';
const FLOOR_PLAN_GEOMETRY_ERROR_MESSAGE =
  'This floor plan could not be analyzed. Try a clearer image with less noise and higher contrast.';

function responseHasImportGeometry(response) {
  return Boolean(
    (Array.isArray(response?.points) && Array.isArray(response?.walls)) ||
      (Array.isArray(response?.vectorGeometry?.points) &&
        Array.isArray(response?.vectorGeometry?.walls)),
  );
}

function buildSelectedRoomAnalysisJson(analysisJson, roomSelection) {
  if (!analysisJson || !roomSelection?.room?.polygon) return analysisJson;
  const roomPolygon = roomSelection.room.polygon.map((point) => [...point]);
  return {
    ...analysisJson,
    targetSpace: {
      ...(analysisJson.targetSpace || {}),
      name: roomSelection.name,
      type: 'room',
      isClosedRoom: true,
      confidence: 'user',
      polygon: roomPolygon,
    },
    selectedRoomId: roomSelection.roomId,
  };
}

function roomBoundsMeters(polygon, metersPerPixel) {
  if (!Array.isArray(polygon) || polygon.length < 2 || !Number.isFinite(metersPerPixel)) {
    return { widthM: 0, depthM: 0 };
  }
  const xs = polygon.map((point) => point[0]);
  const ys = polygon.map((point) => point[1]);
  return {
    widthM: (Math.max(...xs) - Math.min(...xs)) * metersPerPixel,
    depthM: (Math.max(...ys) - Math.min(...ys)) * metersPerPixel,
  };
}

function scheduleFitFloorPlanToViewport(viewportActionsRef, bounds, options = {}) {
  const runFit = () => {
    if (options.resetInteraction !== false) {
      viewportActionsRef.current.resetInteraction?.();
    }
    if (bounds) {
      viewportActionsRef.current.fitBounds?.(bounds, { paddingRatio: 0.12 });
    } else {
      viewportActionsRef.current.fit?.();
    }
  };

  let cancelled = false;
  const schedule = () => {
    if (!cancelled) runFit();
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(schedule);
  });
  const timeoutId = window.setTimeout(schedule, 200);

  return () => {
    cancelled = true;
    window.clearTimeout(timeoutId);
  };
}

function resolveLiveRenderRoom(roomSelection, analysisJson, metersPerPixel) {
  if (!roomSelection) return null;
  const liveRoom = Array.isArray(analysisJson?.rooms)
    ? analysisJson.rooms.find((room) => room?.id === roomSelection.roomId)
    : null;
  if (!liveRoom?.polygon) return roomSelection;
  const bounds = roomBoundsMeters(liveRoom.polygon, metersPerPixel);
  return {
    ...roomSelection,
    widthM: bounds.widthM || roomSelection.widthM,
    depthM: bounds.depthM || roomSelection.depthM,
    room: {
      ...(roomSelection.room || {}),
      polygon: liveRoom.polygon.map((point) => [...point]),
      wallIds: liveRoom.wallIds || roomSelection.room?.wallIds,
      boundaryWallIds: liveRoom.boundaryWallIds || roomSelection.room?.boundaryWallIds,
    },
  };
}

function FloorPlanRoomToRealisticTestPage({ variant = 'test', publicSeo = null }) {
  const isInsideAppLayout = useIsInsideAppLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const isProduct = variant === 'product';
  const shouldShowOpeningChoice = publicSeo?.path === '/ai-tools/ai-2d-floorplan-to-room-render';
  const { user } = useAuth();
  const credits = useCredits();
  const [floorPlanFileName, setFloorPlanFileName] = useState('');
  const [preparedBlob, setPreparedBlob] = useState(null);
  const [preparedPreviewUrl, setPreparedPreviewUrl] = useState('');
  const [designerState, setDesignerState] = useState(() => createEmptyDesignerState());
  const [roomType, setRoomType] = useState(ROOM_TYPES[0]);
  const [style, setStyle] = useState(DESIGN_STYLES[0]);
  const [decorationLevel, setDecorationLevel] = useState(DECORATION_CHANGE_LEVELS[2]);
  const [themeColorSource, setThemeColorSource] = useState('ai');
  const [selectedColors, setSelectedColors] = useState([]);
  const [wallColorSource, setWallColorSource] = useState('ai');
  const [selectedWallColors, setSelectedWallColors] = useState([]);
  const [themeColorsPopupOpen, setThemeColorsPopupOpen] = useState(false);
  const [wallColorsPopupOpen, setWallColorsPopupOpen] = useState(false);
  const [userNotes, setUserNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingMessage, setAnalyzingMessage] = useState(ANALYZING_MESSAGE_DEFAULT);
  const [analyzingWarn, setAnalyzingWarn] = useState('');
  const [readyPromptOpen, setReadyPromptOpen] = useState(false);
  const [openingChoiceOpen, setOpeningChoiceOpen] = useState(shouldShowOpeningChoice);
  const [editorActiveTool, setEditorActiveTool] = useState(
    floorPlanToRoomImageConfig.initialActiveTool || 'select',
  );
  const [error, setError] = useState('');
  const {
    pageMode,
    resultViewMode,
    setResultViewMode,
    currentProject,
    enterOutputMode,
    enterEditMode,
    resetToDesign,
  } = useDesignWorkspaceMode();
  const [downloadError, setDownloadError] = useState('');
  const [zoomedImage, setZoomedImage] = useState(() => null);
  const preparedUrlRef = useRef('');
  const fileInputRef = useRef(null);
  const viewportActionsRef = useRef({});
  const editorActionsRef = useRef(
    /** @type {{ initializeManualDrawingMode?: () => void } | null} */ (null),
  );
  const pendingViewportFitRef = useRef(false);
  const pendingFitOnReturnRef = useRef(false);
  const pendingFitBoundsRef = useRef(null);
  const closedReadyPromptRef = useRef(false);
  const themeColorSectionRef = useRef(null);
  const wallColorSectionRef = useRef(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [confirmedRenderRoom, setConfirmedRenderRoom] = useState(null);
  const [workflowMode, setWorkflowMode] = useState('floorplan');
  const [cameraIndex, setCameraIndex] = useState(0);
  const [cameraPosition, setCameraPosition] = useState(null);
  const [fov, setFov] = useState(DEFAULT_FOV);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('1536x1024');
  const [generatedImageQuality, setGeneratedImageQuality] = useState(DEFAULT_GENERATED_IMAGE_QUALITY);
  const cameraStateRef = useRef(null);
  const joystickInputRef = useRef({ x: 0, y: 0, active: false });
  const captureRectRef = useRef(null);
  const captureActionsRef = useRef(null);
  const generationInFlightRef = useRef(false);
  const [captureReady, setCaptureReady] = useState(false);
  const [captureLayoutVersion, setCaptureLayoutVersion] = useState(0);

  const revokePreparedUrl = useCallback(() => {
    if (preparedUrlRef.current) {
      URL.revokeObjectURL(preparedUrlRef.current);
      preparedUrlRef.current = '';
    }
  }, [designerState]);

  useEffect(() => {
    return () => {
      revokePreparedUrl();
    };
  }, [revokePreparedUrl]);

  useEffect(() => {
    if (!openingChoiceOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [openingChoiceOpen]);

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      revokePreparedUrl();
      setPreparedBlob(null);
      setPreparedPreviewUrl('');
      setDesignerState(createEmptyDesignerState());
      setEditorActiveTool(floorPlanToRoomImageConfig.initialActiveTool || 'select');
      setSelectedRoom(null);
      setConfirmedRenderRoom(null);
      setWorkflowMode('floorplan');
      setCameraPosition(null);
      setReadyPromptOpen(false);
      setError('');

      const objectUrl = URL.createObjectURL(file);
      preparedUrlRef.current = objectUrl;
      setPreparedPreviewUrl(objectUrl);
      setPreparedBlob(file);
      setFloorPlanFileName(file.name);

      if (!HAS_CUBICASA_BACKEND_URL) {
        setError('Floor plan analysis is not configured yet. Please contact support.');
        return;
      }

      const fileValidation = validateCubiCasaFloorPlanFile(file);
      if (!fileValidation.ok) {
        setError(fileValidation.error);
        return;
      }

      setAnalyzingMessage(ANALYZING_MESSAGE_DEFAULT);
      setAnalyzingWarn(fileValidation.warn || '');
      setAnalyzing(true);
      const result = await requestCubiCasaMaskTest({
        file,
        onRetrying: () =>
          setAnalyzingMessage('The image is complex. Retrying with an optimized version...'),
      });
      setAnalyzing(false);
      setAnalyzingMessage(ANALYZING_MESSAGE_DEFAULT);
      setAnalyzingWarn('');

      if (!result.ok) {
        setError(result.error);
        return;
      }
      if (!responseHasImportGeometry(result.raw)) {
        setError(FLOOR_PLAN_GEOMETRY_ERROR_MESSAGE);
        return;
      }

      let designerImport;
      try {
        designerImport = importCubiCasaResponseToDesignerState(result.raw, {
          maxWidthM: 30,
          maxHeightM: 30,
          paddingM: 1,
          offsetX: 1,
          offsetY: 1,
          standardDoorWidthM: 1.0,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load the analyzed floor plan.');
        return;
      }

      const importedDesignerState = normalizeDesignerOpenings({
        canvasWidth: designerImport.canvasWidth || 30,
        canvasHeight: designerImport.canvasHeight || 30,
        points: designerImport.points,
        walls: designerImport.walls,
        openings: designerImport.openings,
        objects: designerImport.objects || [],
        scale: designerImport.scale,
      });
      pendingFitBoundsRef.current =
        getDesignerDrawingBounds(importedDesignerState) || designerImport.scale?.coordinateBounds;
      pendingViewportFitRef.current = true;
      setDesignerState((prev) => ({
        ...prev,
        ...importedDesignerState,
        scale: {
          ...prev.scale,
          ...designerImport.scale,
        },
        activeChainPointIds: [],
        draftPreviewPoint: null,
        roomsMeta: {},
      }));
      setReadyPromptOpen(true);
    },
    [revokePreparedUrl],
  );

  const dismissOpeningChoice = useCallback(() => {
    setOpeningChoiceOpen(false);
    setError('');
    editorActionsRef.current?.initializeManualDrawingMode?.();
  }, []);

  const handleUploadChoice = useCallback(() => {
    if (analyzing || generating) return;
    dismissOpeningChoice();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, [analyzing, generating, dismissOpeningChoice]);

  const handleDrawChoice = useCallback(() => {
    dismissOpeningChoice();
  }, [dismissOpeningChoice]);

  const handleCloseOpeningChoice = useCallback(() => {
    dismissOpeningChoice();
  }, [dismissOpeningChoice]);

  const handleOpeningChoiceBackdropClick = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        dismissOpeningChoice();
      }
    },
    [dismissOpeningChoice],
  );

  useEffect(() => {
    if (!openingChoiceOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        dismissOpeningChoice();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dismissOpeningChoice, openingChoiceOpen]);

  const handleRegisterEditorActions = useCallback((actions) => {
    editorActionsRef.current = actions;
  }, []);

  const handleViewportActionsChange = useCallback((actions) => {
    viewportActionsRef.current = actions;
  }, []);

  useEffect(() => {
    if (
      !pendingViewportFitRef.current ||
      analyzing ||
      (workflowMode !== 'floorplan' && !pendingFitOnReturnRef.current)
    ) {
      return undefined;
    }

    const bounds =
      pendingFitBoundsRef.current ||
      getDesignerDrawingBounds(designerState) ||
      designerState.scale?.coordinateBounds;
    pendingViewportFitRef.current = false;
    pendingFitBoundsRef.current = null;
    const isReturnFit = pendingFitOnReturnRef.current;
    pendingFitOnReturnRef.current = false;

    return scheduleFitFloorPlanToViewport(viewportActionsRef, bounds, {
      resetInteraction: !isReturnFit,
    });
  }, [analyzing, designerState, readyPromptOpen, workflowMode]);

  useEffect(() => {
    if (readyPromptOpen || designerState.points.length === 0 || !closedReadyPromptRef.current) {
      return undefined;
    }

    closedReadyPromptRef.current = false;
    const bounds = getDesignerDrawingBounds(designerState) || designerState.scale?.coordinateBounds;
    if (!bounds) return undefined;

    return scheduleFitFloorPlanToViewport(viewportActionsRef, bounds);
  }, [readyPromptOpen, designerState]);

  const validation = useMemo(() => validateDesignerState(designerState), [designerState]);
  const activeRenderRoom = useMemo(
    () =>
      resolveLiveRenderRoom(
        confirmedRenderRoom,
        validation.analysisJson,
        designerState.scale?.metersPerPixel,
      ),
    [confirmedRenderRoom, validation.analysisJson, designerState.scale?.metersPerPixel],
  );
  const selectedRoomAnalysisJson = useMemo(
    () => buildSelectedRoomAnalysisJson(validation.analysisJson, activeRenderRoom),
    [validation.analysisJson, activeRenderRoom],
  );
  const cameraPresets = useMemo(
    () => buildCameraPresets(activeRenderRoom, designerState.scale?.metersPerPixel),
    [activeRenderRoom, designerState.scale?.metersPerPixel],
  );
  const baseCameraPreset = cameraPresets[cameraIndex] || cameraPresets[0] || null;
  const activeCameraPreset = useMemo(() => {
    if (!baseCameraPreset) return null;
    const position = cameraPosition ?? baseCameraPreset.position;
    return {
      ...baseCameraPreset,
      position,
    };
  }, [baseCameraPreset, cameraPosition]);

  const roomMoveBounds = useMemo(() => {
    const polygon = activeRenderRoom?.room?.polygon;
    const metersPerPixel = designerState.scale?.metersPerPixel;
    if (!Array.isArray(polygon) || polygon.length < 3 || !Number.isFinite(metersPerPixel)) {
      return null;
    }
    return { polygon, metersPerPixel, marginM: CAMERA_WALL_MARGIN_M };
  }, [activeRenderRoom, designerState.scale?.metersPerPixel]);

  const generationCreditCost = generatedImageQuality === GENERATED_IMAGE_QUALITY_HIGH ? 3 : 1;
  const hasInsufficientCredits = credits != null && credits < generationCreditCost;

  const generationValidation = useMemo(() => {
    const captureRect = captureRectRef.current;
    return {
      hasSelectedRoom: Boolean(activeRenderRoom),
      hasValidRoomGeometry:
        Array.isArray(activeRenderRoom?.room?.polygon) && activeRenderRoom.room.polygon.length >= 3,
      hasRoomAnalysis: Boolean(selectedRoomAnalysisJson),
      captureReady,
      hasAspectRatio: Boolean(selectedAspectRatio),
      hasCaptureRect: Boolean(captureRect?.width > 0 && captureRect?.height > 0),
      hasCameraPreset: Boolean(activeCameraPreset),
      hasRoomType: Boolean(roomType),
      hasStyle: Boolean(style),
      hasCredits: credits == null || credits >= generationCreditCost,
      notGenerating: !generating,
    };
  }, [
    activeRenderRoom,
    selectedRoomAnalysisJson,
    captureReady,
    selectedAspectRatio,
    captureLayoutVersion,
    activeCameraPreset,
    roomType,
    style,
    credits,
    generationCreditCost,
    generating,
  ]);

  const canGenerate = Object.values(generationValidation).every(Boolean);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (canGenerate) return;
    console.table(generationValidation);
  }, [canGenerate, generationValidation]);

  const handleCaptureLayoutChange = useCallback(() => {
    setCaptureLayoutVersion((version) => version + 1);
  }, []);

  useEffect(() => {
    if (cameraPresets.length === 0) {
      if (cameraIndex !== 0) setCameraIndex(0);
      return;
    }
    if (cameraIndex >= cameraPresets.length) {
      setCameraIndex(cameraPresets.length - 1);
    }
  }, [cameraIndex, cameraPresets.length]);

  const handleGenerate = useCallback(async () => {
    if (generationInFlightRef.current) return;
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`);
      return;
    }
    if (!activeRenderRoom) {
      setError('Select a room and confirm that you want to render it first.');
      return;
    }
    if (hasInsufficientCredits) {
      setError('You do not have enough credits. Please buy a credit pack to continue.');
      return;
    }

    const captureActions = captureActionsRef.current;
    const captureRect = captureRectRef.current;
    if (!captureActions?.captureViewportRegion || !captureRect?.width || !captureRect?.height) {
      setError('The 3D preview is not ready. Wait for the viewport to load.');
      return;
    }

    generationInFlightRef.current = true;
    setGenerating(true);
    setError('');

    const aspectConfig = cropConfigFromOpenAiSize(selectedAspectRatio);

    let viewportBlob;
    try {
      viewportBlob = await captureActions.captureViewportRegion(
        captureRect,
        aspectConfig.outWidth,
        aspectConfig.outHeight,
      );
    } catch (err) {
      generationInFlightRef.current = false;
      setGenerating(false);
      setError(err instanceof Error ? err.message : 'Could not capture the 3D viewport.');
      return;
    }
    const cameraPayload = cameraStateRef.current
      ? {
          index: cameraIndex,
          fov,
          presetId: activeCameraPreset?.id,
          position: cameraStateRef.current.position,
          target: cameraStateRef.current.target,
          lookDirection: cameraStateRef.current.lookDirection,
          preset: activeCameraPreset,
          aspectRatio: selectedAspectRatio,
          outputWidth: aspectConfig.outWidth,
          outputHeight: aspectConfig.outHeight,
        }
      : {
          index: cameraIndex,
          fov,
          presetId: activeCameraPreset?.id,
          preset: activeCameraPreset,
          aspectRatio: selectedAspectRatio,
          outputWidth: aspectConfig.outWidth,
          outputHeight: aspectConfig.outHeight,
        };

    const projectId = doc(collection(db, 'projects')).id;
    if (process.env.NODE_ENV === 'development') {
      console.log('[floorplan-room-generation]', {
        toolId: 'floorplan_to_room_image',
        requiredCredits: generationCreditCost,
        creditsBefore: credits,
        generationId: projectId,
        billingPathUsed: 'floorplan-room-post-success-settlement',
      });
    }
    const result = await requestFloorPlanRoomToRealisticImage(user, {
      blob: viewportBlob,
      roomType,
      style,
      userNotes,
      projectId,
      selectedRoom: {
        id: activeRenderRoom.roomId,
        name: activeRenderRoom.name,
        widthM: activeRenderRoom.widthM,
        depthM: activeRenderRoom.depthM,
        polygon: activeRenderRoom.room?.polygon,
      },
      camera: cameraPayload,
      analysisJson: selectedRoomAnalysisJson,
      decorationLevel,
      imageQuality: generatedImageQuality,
      colorPreferences: {
        themeColorSource,
        selectedColors,
        wallColorSource,
        selectedWallColors,
      },
    });

    generationInFlightRef.current = false;
    setGenerating(false);

    if (process.env.NODE_ENV === 'development') {
      console.log('[floorplan-room-generation-result]', {
        generationId: projectId,
        chargedCredits: result.ok ? result.chargedCredits : undefined,
        creditsAfter: result.ok ? result.creditsAfter : undefined,
        transactionId: result.ok ? result.transactionId : undefined,
        status: result.ok ? result.billingStatus || 'success' : result.code || 'error',
      });
    }

    if (!result.ok) {
      setError(result.error);
      return;
    }

    const completedProject = result.project
      ? { id: projectId, ...result.project }
      : {
          id: projectId,
          toolType: 'floorplan_to_room_image',
          status: 'completed',
          generatedImageUrl: result.generatedImageUrl || '',
          roomType,
          style,
          notes: userNotes,
          roomLabel: activeRenderRoom.name || roomType,
        };
    if (result.generatedImageUrl) {
      completedProject.generatedImageUrl = result.generatedImageUrl;
    }
    enterOutputMode(completedProject);
  }, [
    user,
    activeRenderRoom,
    roomType,
    style,
    userNotes,
    cameraIndex,
    fov,
    activeCameraPreset,
    selectedRoomAnalysisJson,
    decorationLevel,
    themeColorSource,
    selectedColors,
    wallColorSource,
    selectedWallColors,
    generatedImageQuality,
    selectedAspectRatio,
    credits,
    generationCreditCost,
    hasInsufficientCredits,
    enterOutputMode,
    location.pathname,
    location.search,
    navigate,
  ]);

  const handleRenderRoomRequest = useCallback((room) => {
    setConfirmedRenderRoom(room);
    setWorkflowMode('roomRenderSetup');
    setCameraIndex(0);
    setCameraPosition(null);
    setFov(DEFAULT_FOV);
    setError('');
  }, []);

  const handleSelectAnotherRoom = useCallback(() => {
    pendingFitOnReturnRef.current = true;
    pendingFitBoundsRef.current =
      getDesignerDrawingBounds(designerState) || designerState.scale?.coordinateBounds || null;
    pendingViewportFitRef.current = true;
    setConfirmedRenderRoom(null);
    setSelectedRoom(null);
    setWorkflowMode('floorplan');
    setCameraPosition(null);
    setError('');
    viewportActionsRef.current.clearSelection?.();
  }, []);

  const handleCameraIndexChange = useCallback((index) => {
    setCameraIndex(index);
    setCameraPosition(null);
  }, []);

  const handleCameraStateChange = useCallback((state) => {
    cameraStateRef.current = state;
  }, []);

  const handleRegisterCaptureActions = useCallback((actions) => {
    captureActionsRef.current = actions;
    setCaptureReady(Boolean(actions?.captureViewportRegion));
  }, []);

  const themeColorButtonCaption =
    themeColorSource === 'custom' && selectedColors.length > 0 ? null : 'AI Choice';
  const wallColorButtonCaption =
    wallColorSource === 'custom' && selectedWallColors.length > 0 ? null : 'AI Choice';

  const uploadSidebarContent = (
    <div className="new-design-upload-mobile-summary floorplan-room-render-upload-summary">
      <h3 className="new-design-subheading new-design-upload-section-heading">
        <Upload className="new-design-subheading-icon" size={18} strokeWidth={2} aria-hidden />
        Upload Floor Plan
      </h3>
      <input
        ref={fileInputRef}
        id="floorplan-room-render-upload"
        type="file"
        accept="image/png,image/jpeg"
        onChange={handleFileChange}
        disabled={analyzing || generating}
        className="new-design-file-input"
      />
      <div className="new-design-upload-empty-stack">
        <div className="new-design-upload-dropzone-wrap">
          <label htmlFor="floorplan-room-render-upload" className="upload-dropzone">
            <div className="upload-dropzone-inner">
              <span className="upload-dropzone-cloud" aria-hidden="true">
                <Upload size={38} strokeWidth={1.75} />
              </span>
              <span>{floorPlanFileName || 'Upload your floor plan'}</span>
            </div>
          </label>
        </div>
      </div>
      <p className="floorplan-room-render-sidebar-note">
        JPEG and PNG floor plan images only.
      </p>
      <p className="floorplan-room-render-sidebar-note">
        We will detect walls, doors, windows, and rooms automatically.
      </p>
    </div>
  );

  return (
    <>
      <Seo
        title={
          publicSeo?.title ||
          (isProduct
            ? 'Floor Plan to Room Preview | MyRoomsAI'
            : '2D Floor Plan Room to Realistic Image — Test')
        }
        description={
          publicSeo?.description ||
          (isProduct
            ? 'Draw a 2D floor plan, select a room, and generate a realistic room preview.'
            : 'Convert a cropped 2D floor plan room area into a realistic eye-level interior design image.')
        }
        canonicalPath={
          publicSeo?.path ||
          (isProduct
            ? '/projects/ai-2d-floorplan-to-room-render'
            : '/test/floorplan-room-to-realistic-image')
        }
        noindex={!publicSeo}
        structuredData={
          publicSeo
            ? [...buildPublicToolPageSchemas(publicSeo), getSeoFaqSchema(publicSeo.path)].filter(Boolean)
            : []
        }
      />
      {!isInsideAppLayout ? <Navbar /> : null}
      <main
        className={`dashboard-page new-design-page floorplan-room-render-workflow${
          workflowMode === 'floorplan'
            ? ' floorplan-room-render-workflow--editor'
            : ' floorplan-room-render-workflow--setup floorplan-room-render-page'
        }${pageMode === 'output' ? ' project-detail-page' : ''}`}
      >
        <div className="container">
          {pageMode === 'output' && currentProject ? (
            <>
              <DesignOutputWorkspace
                project={currentProject}
                resultViewMode={resultViewMode}
                onResultViewModeChange={setResultViewMode}
                onNewProject={resetToDesign}
                onEdit={() => enterEditMode()}
                shopItemCount={0}
                onDownloadImage={async (url, baseName) => {
                  setDownloadError('');
                  try {
                    await downloadUrlAsFile(url, baseName, user);
                  } catch (err) {
                    setDownloadError(err?.message || 'Download failed.');
                  }
                }}
                onZoomImage={setZoomedImage}
                downloadError={downloadError}
              />
            </>
          ) : workflowMode === 'floorplan' ? (
            <section className="floorplan-room-render-editor-shell">
              <FloorPlanEditor
                value={designerState}
                onChange={setDesignerState}
                config={floorPlanToRoomImageConfig}
                ariaLabel="AI FloorPlan to realistic room render editor"
                activeTool={editorActiveTool}
                onActiveToolChange={setEditorActiveTool}
                onViewportActionsChange={handleViewportActionsChange}
                onRegisterEditorActions={handleRegisterEditorActions}
                sidebarTopContent={uploadSidebarContent}
                onRoomSelectionChange={setSelectedRoom}
                onRenderRoomRequest={handleRenderRoomRequest}
              />
            </section>
          ) : (
            <RoomRenderSetupLayout
              roomName={activeRenderRoom?.name || selectedRoom?.name || 'Selected room'}
              onSelectAnotherRoom={handleSelectAnotherRoom}
              selectedRoomAnalysisJson={selectedRoomAnalysisJson}
              canvasWidth={designerState.canvasWidth}
              canvasHeight={designerState.canvasHeight}
              activeCameraPreset={activeCameraPreset}
              cameraPresets={cameraPresets}
              cameraIndex={cameraIndex}
              onCameraIndexChange={handleCameraIndexChange}
              fov={fov}
              onFovChange={setFov}
              selectedAspectRatio={selectedAspectRatio}
              onAspectRatioChange={setSelectedAspectRatio}
              onCameraStateChange={handleCameraStateChange}
              joystickInputRef={joystickInputRef}
              captureRectRef={captureRectRef}
              onRegisterCaptureActions={handleRegisterCaptureActions}
              onCaptureLayoutChange={handleCaptureLayoutChange}
              roomMoveBounds={roomMoveBounds}
              roomType={roomType}
              onRoomTypeChange={setRoomType}
              roomTypeOptions={ROOM_TYPES}
              style={style}
              onStyleChange={setStyle}
              styleOptions={DESIGN_STYLES}
              decorationLevel={decorationLevel}
              onDecorationLevelChange={setDecorationLevel}
              decorationLevelOptions={DECORATION_CHANGE_LEVELS}
              themeColorSource={themeColorSource}
              selectedColors={selectedColors}
              themeColorButtonCaption={themeColorButtonCaption}
              onOpenThemeColors={() => setThemeColorsPopupOpen(true)}
              themeColorsPopupOpen={themeColorsPopupOpen}
              wallColorSource={wallColorSource}
              selectedWallColors={selectedWallColors}
              wallColorButtonCaption={wallColorButtonCaption}
              onOpenWallColors={() => setWallColorsPopupOpen(true)}
              wallColorsPopupOpen={wallColorsPopupOpen}
              userNotes={userNotes}
              onUserNotesChange={setUserNotes}
              generatedImageQuality={generatedImageQuality}
              onGeneratedImageQualityChange={setGeneratedImageQuality}
              generating={generating}
              canGenerate={canGenerate}
              hasInsufficientCredits={hasInsufficientCredits}
              onGenerate={handleGenerate}
            />
          )}
          {publicSeo ? <ToolSeoSections {...publicSeo} /> : null}
          {publicSeo ? <CollapsibleSeoContent {...getSeoContent(publicSeo.path)} /> : null}
        </div>
      </main>
      {openingChoiceOpen ? (
        <div
          className="new-design-theme-popup-backdrop floorplan-room-render-opening-backdrop"
          role="presentation"
          onClick={handleOpeningChoiceBackdropClick}
        >
          <div
            className="new-design-theme-popup floorplan-room-render-opening-modal floorplan-start-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="floorplan-room-render-opening-title"
            aria-describedby="floorplan-room-render-opening-copy"
          >
            <button
              type="button"
              className="btn btn-ghost floorplan-room-render-opening-close floorplan-start-close"
              onClick={handleCloseOpeningChoice}
              aria-label="Close start options"
            >
              <X size={18} aria-hidden />
            </button>
            <div className="floorplan-start-modal-content">
              <div className="floorplan-room-render-opening-visual">
                <img
                  src={floorPlanRoomRenderIllustration}
                  alt="2D floor plan to realistic room render illustration"
                  className="floorplan-room-render-opening-img floorplan-start-image"
                  decoding="async"
                />
              </div>
              <div className="floorplan-room-render-opening-content floorplan-start-actions">
                <div>
                  <h2 id="floorplan-room-render-opening-title" className="new-design-theme-popup-title">
                    Start Your Floor Plan Render
                  </h2>
                  <p id="floorplan-room-render-opening-copy" className="floorplan-room-render-sidebar-note">
                    Upload an existing 2D floor plan or draw your room manually to create a realistic room visualization.
                  </p>
                </div>
                <div className="new-design-upload-empty-stack floorplan-room-render-opening-upload-stack">
                  <button
                    type="button"
                    className="upload-dropzone floorplan-room-render-opening-upload"
                    onClick={handleUploadChoice}
                    disabled={analyzing || generating}
                  >
                    <div className="upload-dropzone-inner">
                      <span className="upload-dropzone-cloud" aria-hidden="true">
                        <Upload size={38} strokeWidth={1.75} />
                      </span>
                      <span>{floorPlanFileName || 'Upload your floor plan'}</span>
                      <span className="floorplan-room-render-sidebar-note floorplan-room-render-opening-upload-note">
                        JPEG and PNG floor plan images only.
                      </span>
                    </div>
                  </button>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost floorplan-room-render-opening-draw"
                  onClick={handleDrawChoice}
                >
                  <PencilRuler size={18} aria-hidden />
                  <span>Draw Your Room</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {analyzing ? (
        <div className="new-design-theme-popup-backdrop" role="presentation">
          <div
            className="new-design-theme-popup floorplan-room-render-analysis-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Analyzing floor plan"
          >
            {preparedPreviewUrl ? (
              <img
                src={preparedPreviewUrl}
                alt="Uploaded floor plan"
                className="floorplan-room-render-analysis-img"
                decoding="async"
              />
            ) : null}
            <h2 className="new-design-theme-popup-title">Analyzing your floor plan...</h2>
            <p className="floorplan-room-render-sidebar-note">{analyzingMessage}</p>
            {analyzingWarn ? (
              <p className="floorplan-room-render-sidebar-note" role="status">
                {analyzingWarn}
              </p>
            ) : null}
            <div className="generate-progress-track" aria-hidden="true">
              <div className="generate-progress-fill" />
            </div>
          </div>
        </div>
      ) : null}
      {readyPromptOpen ? (
        <div className="new-design-theme-popup-backdrop" role="presentation">
          <div
            className="new-design-theme-popup floorplan-room-render-ready-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Floor plan ready"
          >
            <h2 id="floorplan-room-render-ready-title" className="new-design-theme-popup-title">
              Your floor plan is ready.
            </h2>
            <p id="floorplan-room-render-ready-copy" className="floorplan-room-render-sidebar-note">
              Edit the floor plan or select a room to generate a realistic image.
            </p>
            <div className="new-design-theme-popup-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  closedReadyPromptRef.current = true;
                  setReadyPromptOpen(false);
                }}
              >
                Start
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="new-design-theme-popup-backdrop" role="presentation">
          <div
            className="new-design-theme-popup floorplan-room-render-ready-modal"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="floorplan-room-render-error-title"
            aria-describedby="floorplan-room-render-error-message"
          >
            <h2 id="floorplan-room-render-error-title" className="new-design-theme-popup-title">
              Error
            </h2>
            <p id="floorplan-room-render-error-message" className="floorplan-room-render-sidebar-note" role="alert">
              {error}
            </p>
            <div className="new-design-theme-popup-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setError('')}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {themeColorsPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={() => {
            themeColorSectionRef.current?.flushPendingAdd();
            setThemeColorsPopupOpen(false);
          }}
        >
          <div
            id="floorplan-room-render-theme-colors-popup"
            className="new-design-theme-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="floorplan-room-render-theme-colors-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="floorplan-room-render-theme-colors-title" className="new-design-theme-popup-title">
                Theme colors
              </h2>
              <button
                type="button"
                className="btn btn-primary new-design-theme-popup-close"
                onClick={() => {
                  themeColorSectionRef.current?.flushPendingAdd();
                  setThemeColorsPopupOpen(false);
                }}
              >
                Done
              </button>
            </div>
            <DesignHexColorSection
              ref={themeColorSectionRef}
              source={themeColorSource}
              onSourceChange={setThemeColorSource}
              maxColors={MAX_CUSTOM_COLORS}
              selectedColors={selectedColors}
              onSelectedColorsChange={setSelectedColors}
              onMarkTouched={() => {}}
              groupAriaLabel="Theme colors"
              aiChoiceHint="Let AI choose a palette that fits the selected room and style."
            />
          </div>
        </div>
      ) : null}
      {wallColorsPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={() => {
            wallColorSectionRef.current?.flushPendingAdd();
            setWallColorsPopupOpen(false);
          }}
        >
          <div
            id="floorplan-room-render-wall-colors-popup"
            className="new-design-theme-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="floorplan-room-render-wall-colors-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="floorplan-room-render-wall-colors-title" className="new-design-theme-popup-title">
                Wall colors
              </h2>
              <button
                type="button"
                className="btn btn-primary new-design-theme-popup-close"
                onClick={() => {
                  wallColorSectionRef.current?.flushPendingAdd();
                  setWallColorsPopupOpen(false);
                }}
              >
                Done
              </button>
            </div>
            <DesignHexColorSection
              ref={wallColorSectionRef}
              source={wallColorSource}
              onSourceChange={setWallColorSource}
              maxColors={MAX_WALL_COLORS}
              selectedColors={selectedWallColors}
              onSelectedColorsChange={setSelectedWallColors}
              onMarkTouched={() => {}}
              groupAriaLabel="Wall colors"
              aiChoiceHint="Let AI choose wall colors that fit the selected room and style."
            />
          </div>
        </div>
      ) : null}
      {zoomedImage ? (
        <div
          className="image-lightbox-backdrop"
          role="presentation"
          onClick={() => setZoomedImage(null)}
        >
          <div
            className="image-lightbox-content"
            role="dialog"
            aria-modal="true"
            aria-label="Image preview"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="image-lightbox-close"
              aria-label="Close image preview"
              onClick={() => setZoomedImage(null)}
            >
              Close
            </button>
            <img
              src={zoomedImage.src}
              alt={zoomedImage.alt}
              className="image-lightbox-img"
              loading="eager"
              decoding="sync"
            />
          </div>
        </div>
      ) : null}
      {!isInsideAppLayout ? <Footer /> : null}
    </>
  );
}

export default FloorPlanRoomToRealisticTestPage;
